import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { apiGet } from "../api/client";
import type { CounterStrikeMap, CounterStrikeMatchResponse, CounterStrikeTeamStats, LegacyMatch } from "../api/legacyTypes";
import { EntityLink } from "./EntityLink";

function mapScore(map: CounterStrikeMap) {
  if (map.status === "unused") return "Not played";
  if (map.status !== "completed") return "Scheduled";
  return `${map.team1Score} : ${map.team2Score}`;
}

export function CounterStrikeMatchPreview({ match, tournamentId, onClose }: { match: LegacyMatch; tournamentId: string; onClose: () => void }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  const slug = tournamentId.split("/").at(-1) ?? "";
  const href = `/tournaments/counterstrike/${slug}/matches/${encodeURIComponent(match.sourceKey)}`;
  const query = useQuery({
    queryKey: ["counterstrike-match-preview", slug, match.sourceKey],
    queryFn: ({ signal }) => apiGet<CounterStrikeMatchResponse>(`/api/v1/tournaments/counterstrike/${slug}/matches/${encodeURIComponent(match.sourceKey)}`, signal),
    staleTime: 60_000,
  });
  const shown = query.data?.match ?? match;
  const maps = shown.counterStrike?.maps ?? [];
  const stats = shown.counterStrike?.stats;
  return <div className="match-preview-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="match-preview cs-match-preview" role="dialog" aria-modal="true" aria-label={`${match.participant1} versus ${match.participant2} quick stats`}>
      <header>
        <div><small>{shown.round || shown.bracket}</small><strong>Best of {shown.counterStrike?.bestOf || 3}</strong></div>
        <button type="button" aria-label="Close match preview" onClick={onClose}>×</button>
      </header>
      <div className="match-preview-score">
        <EntityLink gameId="counterstrike" kind="team" name={shown.participant1 || "TBD"} entityId={shown.teamId1} logoUrl={shown.logo1} />
        <span><b>{shown.score1 || "–"}</b><em>:</em><b>{shown.score2 || "–"}</b></span>
        <EntityLink gameId="counterstrike" kind="team" name={shown.participant2 || "TBD"} entityId={shown.teamId2} logoUrl={shown.logo2} />
      </div>
      {stats?.teams.length ? <div className="cs-preview-telemetry">
        {stats.teams.map((team: CounterStrikeTeamStats) => <article key={team.providerId || team.name}>
          <strong>{team.name}</strong><span><b>{team.kills}</b> K</span><span><b>{team.deaths}</b> D</span><span><b>{team.assists}</b> A</span><span><b>{team.headshots}</b> HS</span>
          {team.players.length > 0 && <small>Top: {[...team.players].sort((a, b) => b.kills - a.kills)[0].name} · {[...team.players].sort((a, b) => b.kills - a.kills)[0].kills} kills</small>}
        </article>)}
      </div> : query.isPending ? <p className="match-preview-empty">Checking GRID for official match telemetry…</p> : <p className="match-preview-empty">{query.data?.statsError?.includes("not configured") ? "GRID telemetry is ready for an Open Access key." : "Official detailed telemetry is not available for this series yet."}</p>}
      {maps.length > 0 ? <div className="cs-preview-maps">
        {maps.map((map) => <article className={`cs-preview-map cs-map-${map.status}`} key={map.number}>
          <span>Map {map.number}</span><strong>{map.name || "TBD"}</strong><b>{mapScore(map)}</b>
          {map.status === "completed" && <small>CT/T split · {map.team1CT}/{map.team1T} — {map.team2CT}/{map.team2T}{map.overtimes.length ? ` · ${map.overtimes.length} OT` : ""}</small>}
        </article>)}
      </div> : <p className="match-preview-empty">Map picks and scores will appear after Liquipedia publishes them.</p>}
      <a className="button-primary match-preview-open" href={href}>Open detailed match</a>
    </aside>
  </div>;
}
